import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Box, TextField, Typography, Button } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

import proposalsServices from '../../../services/proposalsServices';
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';

const ProposalEquipmentOverviewTable = ({ proposal, id, refresh }) => {
  const [equipmentOverview, setEquipmentOverview] = useState('');
  const dirtyRef = useRef(false);
  const lastSavedValueRef = useRef(null);

  // Auto-save function — no refresh() to avoid overwriting active edits
  const saveEquipmentOverview = useCallback(async (data) => {
    await proposalsServices.update(id, {
      equipmentOverview: data || null,
    });
    lastSavedValueRef.current = data || null;
    dirtyRef.current = false;
  }, [id]);

  // Auto-save on word boundaries (space/period) and on blur — no timer
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    markPending,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveEquipmentOverview);

  // Latest value for keyup handlers (state updates are async)
  const latestValueRef = useRef('');

  const saveNowSafe = useCallback((data) => {
    saveNow(data).catch(() => {
      toast.error('Auto-save failed. Your changes will be retried on the next edit.');
    });
  }, [saveNow]);

  useEffect(() => {
    const serverValue = proposal?.equipmentOverview || '';
    const isExternalChange = serverValue !== (lastSavedValueRef.current || '');
    if (!dirtyRef.current || isExternalChange) {
      setEquipmentOverview(serverValue);
      latestValueRef.current = serverValue;
      dirtyRef.current = false;
    }
  }, [proposal?.equipmentOverview]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    dirtyRef.current = true;
    setEquipmentOverview(newValue);
    latestValueRef.current = newValue;
    markPending(newValue);
  };

  // Word boundary: space or period commits the typed text immediately
  const handleKeyUp = (e) => {
    if (e.key === ' ' || e.key === '.') {
      saveNowSafe(latestValueRef.current);
    }
  };

  const handleClear = async () => {
    setEquipmentOverview('');
    dirtyRef.current = false;
    lastSavedValueRef.current = null;
    try {
      await proposalsServices.update(id, { equipmentOverview: null });
      if (refresh) refresh();
    } catch (err) {
      console.error('Clear error:', err);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Describe the equipment being serviced and the work to be performed
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {equipmentOverview && (
            <Button
              size="small"
              color="error"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              disabled={isSaving}
            >
              Clear
            </Button>
          )}
          <AutoSaveIndicator
            isSaving={isSaving}
            hasUnsavedChanges={hasUnsavedChanges}
            lastSaved={lastSaved}
            error={error}
            getLastSavedText={getLastSavedText}
            variant="chip"
          />
        </Box>
      </Box>
      <TextField
        fullWidth
        multiline
        rows={6}
        placeholder="e.g., AC unit model XYZ123 - Inspection and cleaning of condenser coils, checking refrigerant levels, testing electrical connections..."
        value={equipmentOverview}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onBlur={() => saveNowSafe()}
        variant="outlined"
      />
    </Box>
  );
};

export default ProposalEquipmentOverviewTable;
